<!--
 * Path: packages/frontend/src/features/browser/forms/relations/RelationsRead.vue
 * Task: Generic component to display a single-select relation in read-only mode.
 * Update: Now checks context. Opens in pane-right if primary, opens in Modal if already in pane-right.
-->
<template>
  <div class="relations-read">
    <SubHeader v-if="!hideHeader" :title="field.name" :info="field.info" @click:info="$emit('show-info')" />
    <div v-if="!selectedTarget" class="text-body-2 text-medium-emphasis font-italic">
      No entity assigned.
    </div>
    <div v-else class="d-flex align-center hover-pointer mt-1" @click="openTarget">
      <EntityIcon :entity="selectedTarget" size="32" class="me-3" />
      <span class="text-body-1 font-weight-medium text-high-emphasis">{{ selectedTarget.title }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue';
import { useDatastoreStore } from '@/store/datastore';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { FormContextKey } from '@/core/keys';
import type { Entity } from '@/core/types/editor';
import SubHeader from '@/core/components/forms/SubHeader.vue';
import EntityIcon from '@/core/components/common/EntityIcon.vue';

interface FieldProps {
  id: string;
  name: string;
  info?: string;
  entitySource?: string;
}

const props = withDefaults(defineProps<{
  field: FieldProps;
  hideHeader?: boolean;
}>(), {
  hideHeader: false
});

const emit = defineEmits<{ (e: 'show-info'): void; }>();

const datastoreStore = useDatastoreStore();
const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();

const formContext = inject(FormContextKey, null);
const isSecondaryContext = computed<boolean>(() => formContext ? formContext.isSecondary : false);

const activeEntity = computed<Entity | null>(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);

const selectedTarget = computed<Entity | null>(() => {
  if (!activeEntity.value) return null;
  const targetId = (activeEntity.value as any)[props.field.id];
  if (!targetId) return null;
  return datastoreStore.allEntities.find(item => item.id === targetId) || null;
});

const openTarget = () => {
  if (selectedTarget.value) {
    if (isSecondaryContext.value) {
      // Wenn wir bereits im Secondary Panel sind, nutze das Preview Modal
      workspaceStore.setPreviewEntity(selectedTarget.value.id);
    } else {
      // Wenn wir in der Hauptansicht sind, öffne das Secondary Panel
      workspaceStore.setSecondarySelectedEntity(selectedTarget.value.id);
      uiStore.panels.showSecondaryDetails = true;
    }
  }
};
</script>

<style scoped>
.hover-pointer {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.hover-pointer:hover {
  opacity: 0.8;
}

.hover-pointer:hover span.text-body-1 {
  color: rgb(var(--v-theme-primary)) !important;
  text-decoration: underline;
}
</style>
